/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { CSS_BORDER_TYPE, CSS_PROPERTY, getCssBasedOnRtl, media } from '../../styles';
import { ComponentChild, FunctionalComponent, h } from 'preact';
import styled, { DefaultTheme, css } from 'styled-components';
import { UI_VARIANT } from '@usercentrics/cmp-browser-sdk';

export const Border = styled.div`
  border-bottom: 1px solid ${({ theme }): string => theme.typography.color.tertiary};
  margin: 0;

  ${({ theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        margin: 0;
      }
    `}
`;

export const Container = styled.div`
  background-color: white;
  color: ${({ theme }) => theme.typography.color.primary};
  border-radius: 4px;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.06), 0 2px 1px -1px rgba(0, 0, 0, 0.04), 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  display: flex;
  padding: 2px;
  flex: initial;
  -ms-flex: 1 0 auto;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.base.sm};
  max-width: 100%;
  width: 100%;

  ${({ theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        padding: 0;
      }
    `}
`;

type ToggleProps = {
  hasToggle?: boolean;
  hideToggles?: boolean;
};

const resolveDisplay = (hasToggle: boolean | undefined, hideToggles: boolean | undefined) => {
  if (hideToggles) {
    return 'inline-flex';
  }

  if (hasToggle) {
    return 'block';
  }

  return 'flex';
};

export const CardHeader = styled.div<ToggleProps>`
  display: flex;
  flex-direction: row;

  ${({ hasToggle, hideToggles, theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        align-items: center;
        display: ${resolveDisplay(hasToggle, hideToggles)};
      }
    `}
`;

const resolveBorder = (hasToggle: boolean | undefined, hideToggles: boolean | undefined, tertiaryColor: string) => {
  if (hideToggles || !hasToggle) {
    return 'none';
  }

  return `1px solid ${tertiaryColor}`;
};

export const TextContainer = styled.button<ToggleProps>`
  ${({ theme }) => getCssBasedOnRtl(theme, CSS_PROPERTY.TEXT_ALIGNMENT_LEFT)};
  background-color: white;
  border: none;
  display: flex;
  font-size: ${({ theme }): string => theme.typography.size.medium};
  flex: 1 1 40%;
  flex-direction: column;
  ${({ theme }) => getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_RIGHT, theme.spacing.base.xs)};
  outline: none;
  padding: ${({ theme }) => theme.spacing.base.sm};
  word-break: break-word;
  vertical-align: middle;

  &:focus-visible {
    outline-style: auto;
    outline-width: 5px;
  }

  ${({ hasToggle, hideToggles, theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        border-bottom: ${resolveBorder(hasToggle, hideToggles, theme.typography.color.tertiary)};
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        margin: 0;
        padding: ${theme.spacing.mobile.xl};
        width: 100%;
      }
    `}
`;

type TitleProps = {
  useBoldTitle: boolean;
};

export const Title = styled.div<TitleProps>`
  color: ${({ theme }): string => theme.typography.color.primary};
  font-weight: ${({ theme, useBoldTitle }): number =>
    useBoldTitle ? theme.typography.weight.bold : theme.typography.weight.regular};
  margin-top: 2px;

  ${({ theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        margin: 0;
      }
    `}
`;

export const InitialText = styled.div`
  ccolor: ${({ theme }): string => theme.typography.color.primary};
  margin-top: ${({ theme }) => theme.spacing.base.xxs};
`;

export const Label = styled.div`
  color: ${({ theme }): string => theme.typography.color.primary};
  display: flex;
  font-size: ${({ theme }): string => theme.typography.size.small};
  margin-top: ${({ theme }) => theme.spacing.base.xxs};
  width: 100%;
`;

type ColumnRightProps = {
  hideToggles?: boolean;
};

export const ColumnRight = styled.div<ColumnRightProps>`
  align-items: center;
  display: flex;

  ${({ hideToggles, theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        justify-content: space-between;
        height: ${hideToggles ? '100%' : 'initial'};
        ${getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_LEFT, theme.spacing.base.sm)};
      }
    `}
`;

export const Button = styled.button<ToggleProps>`
  align-items: center;
  background-color: white;
  border: none;
  display: flex;
  height: 100%;
  justify-content: center;
  ${({ theme }) => getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_LEFT, theme.spacing.base.xs)};
  ${({ theme }) => getCssBasedOnRtl(theme, CSS_PROPERTY.MARGIN_RIGHT, theme.spacing.base.xs)};
  outline: none;
  padding: ${({ theme }) => theme.spacing.base.sm};
  width: 20px;

  &:focus-visible {
    outline-style: auto;
    outline-width: 5px;
  }

  ${({ hasToggle, hideToggles, theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        ${getCssBasedOnRtl(theme, CSS_PROPERTY.BORDER_RADIUS_BOTTOM_RIGHT, 4)};
        ${hasToggle
          ? getCssBasedOnRtl(theme, CSS_PROPERTY.BORDER_LEFT, 1, CSS_BORDER_TYPE.NONE, theme.typography.color.tertiary)
          : getCssBasedOnRtl(theme, CSS_PROPERTY.BORDER_LEFT, 0, CSS_BORDER_TYPE.NONE)};
        height: auto; // IE11 fix
        height: ${hideToggles ? '100%' : 'initial'};
        margin: 0;
        padding: 10px;
        width: auto; // IE11 fix
        width: initial;
      }

      ${media.mobilePortraitXS} {
        ${getCssBasedOnRtl(theme, CSS_PROPERTY.BORDER_LEFT, 0, CSS_BORDER_TYPE.NONE)};
      }
    `}
`;

export const IconContainer = styled.div`
  align-items: center;
  display: flex;
  height: 15px;
  justify-content: center;
  width: 15px;
  margin-bottom: 6px;
  ${({ theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        height: 24px;
        width: 24px;
      }
    `};
`;

const arrowStyle = (theme: DefaultTheme): string => {
  return `
    border: solid ${theme.typography.color.secondary};
    padding: ${theme.spacing.base.xxs};
    border-width: 0 1.5px 1.5px 0;
    height: 8px;
    width: 8px;
`;
};

export const UpArrow = styled.i`
  transform: translate(0, 3.3px) rotate(-135deg);
  ${({ theme }) => arrowStyle(theme)}
`;

export const DownArrow = styled.i`
  transform: rotate(45deg);
  ${({ theme }) => arrowStyle(theme)}
`;

export const ChildContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.base.sm};
  flex: auto;
  flex-wrap: wrap;
  max-width: 100%;
  max-height: 100%;

  ${({ theme }) =>
    theme.uiVariant !== UI_VARIANT.CCPA &&
    css`
      ${media.mobilePortrait} {
        padding: ${theme.spacing.mobile.xl};
      }
    `}
`;

const RowComponent = styled.div<RowProps>`
  align-items: center;
  display: flex;
  flex-direction: row;

  ${media.mobilePortraitXS} {
    ${({ theme }): string =>
      getCssBasedOnRtl(theme, CSS_PROPERTY.BORDER_RIGHT, 1, CSS_BORDER_TYPE.SOLID, theme.typography.color.tertiary)};
    flex: 2;
    flex-wrap: wrap;

    > div {
      margin-top: ${({ theme }) => theme.spacing.mobile.lg};
    }

    div:last-child {
      margin-bottom: ${({ theme }) => theme.spacing.mobile.lg};
    }
  }
`;

type RowProps = {
  children: ComponentChild[];
  reverseDirection?: boolean;
};

export const Row: FunctionalComponent<RowProps> = ({ children, reverseDirection = false }) => {
  return <RowComponent>{reverseDirection ? children.reverse() : children}</RowComponent>;
};
